export const CLIENT_SSO_GROUP = "client";
export const CLIENT_SSO_ENTRY_PATH = "/generative";
export const CLIENT_SSO_BASE_PATH = "/generative-platform";
export const CLIENT_SSO_IMAGE_GENERATOR_PATH =
  "/generative-platform/image-generator";
// Default post-login landing only. Aliases Image Generator — never use for
// Collection Build route detection, Recents, openSession, or product gating.
export const CLIENT_SSO_HOME_PATH = CLIENT_SSO_IMAGE_GENERATOR_PATH;
export const CLIENT_SSO_COLLECTION_BUILD_PATH =
  "/generative-platform/collection-build";
export const CLIENT_SSO_LIBRARY_PATH = "/generative-platform/library";
export const CLIENT_SSO_AUTH_FLAG_KEY = "ich_auth_source";
export const CLIENT_SSO_SESSION_MESSAGE_KEY = "ich_client_sso_session_message";
export const CLIENT_SSO_HANDSHAKE_TOKEN_KEY = "ich_client_sso_handshake_token";
export const CLIENT_SSO_LOGIN_LOCK_KEY = "ich_client_sso_login_lock";
export const CLIENT_SSO_RETURN_URL_KEY = "ich_client_sso_return_url";
export const CLIENT_SSO_REVALIDATE_INTERVAL_MS = 30 * 60 * 1000;
/** Fallback when SSO did not pass returnUrl / env is unset */
export const DEFAULT_CLIENT_PLATFORM_RETURN_URL =
  "https://ichnext.ichcreative.com/";

export const isClientSsoUser = (group?: string) => group === CLIENT_SSO_GROUP;

export const isClientSsoAllowedPath = (pathname: string) =>
  pathname === CLIENT_SSO_ENTRY_PATH ||
  pathname === CLIENT_SSO_BASE_PATH ||
  pathname.startsWith(`${CLIENT_SSO_BASE_PATH}/`);

export const isClientSsoHandshakePath = () => {
  const pathname = window.location.pathname.replace(/\/$/, "") || "/";
  if (pathname !== CLIENT_SSO_ENTRY_PATH) {
    return false;
  }
  return Boolean(
    new URLSearchParams(window.location.search).get("token") ||
      sessionStorage.getItem(CLIENT_SSO_HANDSHAKE_TOKEN_KEY)
  );
};

export const isSafeReturnUrl = (value: string) => {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

export const captureClientPlatformReturnUrl = () => {
  const fromQuery =
    new URLSearchParams(window.location.search).get("returnUrl") ||
    new URLSearchParams(window.location.search).get("return");
  if (fromQuery && isSafeReturnUrl(fromQuery)) {
    sessionStorage.setItem(CLIENT_SSO_RETURN_URL_KEY, fromQuery);
  }
};

export const getClientPlatformReturnUrl = () => {
  const stored = sessionStorage.getItem(CLIENT_SSO_RETURN_URL_KEY);
  if (stored && isSafeReturnUrl(stored)) {
    return stored;
  }
  const fromEnv = (
    process.env.REACT_APP_CLIENT_PLATFORM_RETURN_URL || ""
  ).trim();
  if (fromEnv && isSafeReturnUrl(fromEnv)) {
    return fromEnv;
  }
  return DEFAULT_CLIENT_PLATFORM_RETURN_URL;
};

export const redirectToClientPlatform = () => {
  const url = getClientPlatformReturnUrl();
  if (!url || !isSafeReturnUrl(url)) {
    return false;
  }
  window.location.assign(url);
  return true;
};

export const setClientSsoAuthFlag = () => {
  sessionStorage.setItem(CLIENT_SSO_AUTH_FLAG_KEY, "client_sso");
};

export const clearClientSsoAuthFlag = () => {
  sessionStorage.removeItem(CLIENT_SSO_AUTH_FLAG_KEY);
};

export const isClientSsoAuthSession = () =>
  sessionStorage.getItem(CLIENT_SSO_AUTH_FLAG_KEY) === "client_sso";

export const markClientSsoSessionEnded = (message: string) => {
  clearClientSsoAuthFlag();
  sessionStorage.removeItem(CLIENT_SSO_HANDSHAKE_TOKEN_KEY);
  sessionStorage.removeItem(CLIENT_SSO_LOGIN_LOCK_KEY);
  sessionStorage.setItem(CLIENT_SSO_SESSION_MESSAGE_KEY, message);
};

export const consumeClientSsoSessionMessage = () => {
  const message = sessionStorage.getItem(CLIENT_SSO_SESSION_MESSAGE_KEY);
  if (message) {
    sessionStorage.removeItem(CLIENT_SSO_SESSION_MESSAGE_KEY);
  }
  return message;
};

export const takeHandshakeToken = () => {
  captureClientPlatformReturnUrl();
  const fromUrl = new URLSearchParams(window.location.search).get("token");
  if (fromUrl) {
    sessionStorage.removeItem(CLIENT_SSO_LOGIN_LOCK_KEY);
    sessionStorage.setItem(CLIENT_SSO_HANDSHAKE_TOKEN_KEY, fromUrl);
    window.history.replaceState({}, "", CLIENT_SSO_ENTRY_PATH);
    return fromUrl;
  }
  return sessionStorage.getItem(CLIENT_SSO_HANDSHAKE_TOKEN_KEY);
};

export const clearHandshakeToken = () => {
  sessionStorage.removeItem(CLIENT_SSO_HANDSHAKE_TOKEN_KEY);
};
